import React, { Suspense, useEffect, useMemo, useState } from "react";
import { ProductFruits } from "react-product-fruits";
import { useLocation } from "react-router-dom";

import { App as AntApp, ConfigProvider } from "antd";
import csCZ from "antd/locale/cs_CZ";
import deDE from "antd/locale/de_DE";
import enGB from "antd/locale/en_GB";
import enUS from "antd/locale/en_US";
import huHU from "antd/locale/hu_HU";
import plPL from "antd/locale/pl_PL";
import skSK from "antd/locale/sk_SK";
import dayjs from "dayjs";
import "dayjs/locale/cs";
import "dayjs/locale/de";
import "dayjs/locale/en-gb";
import "dayjs/locale/hu";
import "dayjs/locale/pl";
import "dayjs/locale/sk";

import {
  GeneralContextConsumer,
  GeneralContextProvider,
  IUBENDA_CONSENT_UPDATED_EVENT,
  NavContext,
  NavContextProvider,
  UserContextConsumer,
  UserContextProvider,
  checkMeasurementCookieConsent,
  getLanguageFromLocalStorage,
  initAnalytics,
  initCookieBanner,
  productAnalytics,
  toggleCookiesPreferencesButtonVisibility,
  useAppType,
  useCookieConsent,
  useGetFarms,
  useLanguage,
  useNetworkState,
} from "utils";

import { LOCALE_DICT } from "consts";

import { ErrorBoundary, Layout } from "components";

import { AnalyticsUpdater } from "pages/app/_app/components/AnalyticsUpdater";
import { AppNavBottom } from "pages/app/_app/components/AppNavBottom";
import { HeaderMobile } from "pages/app/_app/components/HeaderMobile";
import { FeatureFlagsReadyGate } from "pages/app/_app/components/feature-flag/FeatureFlagsReadyGate";
import { HeaderDesktop } from "pages/app/_app/components/header-desktop/HeaderDesktop";
import { ImpersonationBanner } from "pages/app/_app/components/impersonation-banner/ImpersonationBanner";
import { RailNav } from "pages/app/_app/components/rail-nav/RailNav";
import { TermsOverlay } from "pages/app/_app/components/terms-overlay/TermsOverlay";
import { AppRoutes } from "pages/app/_app/routes";
import { Offline } from "pages/app/account/Offline";
import { CropMixComposerContextProvider } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";

// The floating cookie preferences button is only shown to logged-out users; logged-in users use the settings page
const SHOW_COOKIE_PREFERENCES_BUTTON_PATHS = ["/sign-in", "/sign-up", "/soilsight"];

const { Content, Header } = Layout;

export const App = () => {
  const appType = useAppType();
  const network = useNetworkState();
  const location = useLocation();
  const { data: farms } = useGetFarms();

  useLanguage();

  const cookiesAccepted = useCookieConsent();
  const hasMeasurementConsent = useCookieConsent(checkMeasurementCookieConsent);
  const [initializedAnalyticsWithConsent, setInitializedAnalyticsWithConsent] = useState(false);
  const [programTermsConsentMissing, setProgramTermsConsentMissing] = useState(false);

  const language = getLanguageFromLocalStorage({ forDates: true });

  const { antdLocale } = useMemo(() => {
    const localeMap = {
      de: { antd: deDE, dayjs: "de" },
      pl: { antd: plPL, dayjs: "pl" },
      cs: { antd: csCZ, dayjs: "cs" },
      sk: { antd: skSK, dayjs: "sk" },
      hu: { antd: huHU, dayjs: "hu" },
      "en-us": { antd: enUS, dayjs: "en" },
      "en-gb": { antd: enGB, dayjs: "en-gb" },
    };

    const currentLocale = LOCALE_DICT[language] || LOCALE_DICT.de;
    const selectedLocale = localeMap[currentLocale] || localeMap.de;

    dayjs.locale(selectedLocale.dayjs);

    return {
      antdLocale: selectedLocale.antd,
    };
  }, [language]);

  useEffect(() => {
    // First-load banner setup. Subsequent language changes re-init the banner from within changeLanguage
    // (after the language has fully persisted), so no language-change listener is needed here.
    initCookieBanner();
  }, []);

  useEffect(() => {
    const updateCookiePreferencesButtonVisibility = () =>
      toggleCookiesPreferencesButtonVisibility(SHOW_COOKIE_PREFERENCES_BUTTON_PATHS.some((path) => location.pathname.includes(path)));

    updateCookiePreferencesButtonVisibility();

    // Iubenda shows its button again once the preferences modal is saved, so re-apply the current page's rule
    window.addEventListener(IUBENDA_CONSENT_UPDATED_EVENT, updateCookiePreferencesButtonVisibility);
    return () => window.removeEventListener(IUBENDA_CONSENT_UPDATED_EVENT, updateCookiePreferencesButtonVisibility);
  }, [location.pathname]);

  useEffect(() => {
    if (farms) {
      setProgramTermsConsentMissing(farms.some((farm) => farm.carbonProgram && !farm.hasAcceptedLatestProgramTerms));
    }
  }, [farms]);

  // only start tracking once the user has actually consented, never before
  useEffect(() => {
    if (cookiesAccepted && !initializedAnalyticsWithConsent) {
      initAnalytics();
      setInitializedAnalyticsWithConsent(true);
    }
  }, [cookiesAccepted, initializedAnalyticsWithConsent]);

  // PostHog is measurement, so it only needs that purpose, not the full consent the legacy trackers wait for.
  useEffect(() => {
    if (hasMeasurementConsent) productAnalytics.init();
  }, [hasMeasurementConsent]);

  // layout when user is offline
  if (!network) {
    return (
      <ConfigProvider theme={{ hashed: false }} locale={antdLocale}>
        <AntApp>
          <Layout>
            <Content>
              <ErrorBoundary>
                <Offline />
              </ErrorBoundary>
            </Content>
          </Layout>
        </AntApp>
      </ConfigProvider>
    );
  }

  // layout when user is on desktop
  if (appType.desktop) {
    return (
      <ConfigProvider theme={{ hashed: false }} locale={antdLocale}>
        <AntApp>
          <Layout className="app-layout">
            <Suspense fallback={<></>}>
              <GeneralContextProvider>
                <GeneralContextConsumer>
                  {({ railNavCollapsed }) => (
                    <UserContextProvider>
                      <ImpersonationBanner />
                      <UserContextConsumer>
                        {({ termsOverlayHidden, user }) =>
                          user && !user?.impersonation && (!user?.hasAcceptedLatestPlatformTerms || programTermsConsentMissing) && !termsOverlayHidden ? (
                            <TermsOverlay />
                          ) : (
                            <CropMixComposerContextProvider>
                              <NavContextProvider>
                                <FeatureFlagsReadyGate>
                                  <ErrorBoundary>
                                    <RailNav />
                                  </ErrorBoundary>
                                  <Layout className={user ? "with-rail-nav" : ""}>
                                    <NavContext.Consumer>
                                      {({ showNavbar }) => (
                                        <>
                                          {user && (
                                            <Header className={railNavCollapsed || !appType?.largeDesktop ? "" : "menu-open"}>
                                              <AnalyticsUpdater /> {/* analytics updater - only render when user is logged in */}
                                              <ErrorBoundary>
                                                <HeaderDesktop />
                                              </ErrorBoundary>
                                            </Header>
                                          )}
                                          <Content className={user && showNavbar ? "with-navbar" : ""}>
                                            {user?.id && process.env.REACT_APP_PRODUCT_FRUITS_KEY && cookiesAccepted ? (
                                              <ProductFruits
                                                workspaceCode={process.env.REACT_APP_PRODUCT_FRUITS_KEY ?? ""}
                                                language={language.substring(0, 2)}
                                                user={{
                                                  username: user?.id,
                                                  firstname: user?.firstName,
                                                  role: user?.farm?.accountingMethodology,
                                                }}
                                              />
                                            ) : null}
                                            <ErrorBoundary>
                                              <AppRoutes />
                                            </ErrorBoundary>
                                          </Content>
                                        </>
                                      )}
                                    </NavContext.Consumer>
                                  </Layout>
                                </FeatureFlagsReadyGate>
                              </NavContextProvider>
                            </CropMixComposerContextProvider>
                          )
                        }
                      </UserContextConsumer>
                    </UserContextProvider>
                  )}
                </GeneralContextConsumer>
              </GeneralContextProvider>
            </Suspense>
          </Layout>
        </AntApp>
      </ConfigProvider>
    );
  }

  // layout if user is on mobile
  return (
    <ConfigProvider theme={{ hashed: false }} locale={antdLocale}>
      <AntApp>
        <Layout>
          <Suspense fallback={<></>}>
            <GeneralContextProvider>
              <UserContextProvider>
                <ImpersonationBanner />
                <UserContextConsumer>
                  {({ termsOverlayHidden, user }) =>
                    user && !user?.impersonation && (!user?.hasAcceptedLatestPlatformTerms || programTermsConsentMissing) && !termsOverlayHidden ? (
                      <TermsOverlay />
                    ) : (
                      <NavContextProvider>
                        <ErrorBoundary>
                          <HeaderMobile />
                        </ErrorBoundary>
                        <Content>
                          <ErrorBoundary>
                            <AppRoutes />
                          </ErrorBoundary>
                        </Content>
                        <ErrorBoundary>
                          <AppNavBottom />
                        </ErrorBoundary>
                      </NavContextProvider>
                    )
                  }
                </UserContextConsumer>
              </UserContextProvider>
            </GeneralContextProvider>
          </Suspense>
        </Layout>
      </AntApp>
    </ConfigProvider>
  );
};
